import React, { useState, useEffect } from "react";
import { getJobs } from "../services/api";
import axios from "axios";
import { ArrowRight, X, CheckCircle2, Send } from "lucide-react";

const ApplicationForm = ({ job, onClose, onSubmitSuccess }) => {
  const [formData, setFormData] = useState({
    applicant_name: '',
    email: '',
    phone: '',
    years_exp: '',
    cover_letter: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL || "";
      await axios.post(`${apiUrl}/api/jobs/${job.id}/apply`, {
        ...formData,
        years_exp: parseInt(formData.years_exp) || 0,
      });
      onSubmitSuccess();
    } catch (err) {
      alert('Failed to submit application. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4" onClick={onClose}>
      <div className="apple-glass rounded-3xl p-8 max-w-xl w-full border border-white/20 shadow-2xl relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-6 right-6 text-[#86868b] hover:text-white p-1 rounded-full hover:bg-white/10 transition-colors">
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <span className="text-[10px] font-mono uppercase tracking-widest text-blue-400">Position Application</span>
          <h2 className="text-2xl font-bold text-white font-heading mt-1">{job.title}</h2>
          <p className="text-xs text-[#86868b] mt-1">Join the engineering team at QuivixDigital.</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-mono text-[#86868b] block mb-1">Full Name</label>
              <input
                type="text"
                name="applicant_name"
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                placeholder="Jane Doe"
                value={formData.applicant_name}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="text-[11px] font-mono text-[#86868b] block mb-1">Email Address</label>
              <input
                type="email"
                name="email"
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                placeholder="applicant@quivixdigital.com"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] font-mono text-[#86868b] block mb-1">Phone Number</label>
              <input
                type="tel"
                name="phone"
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                placeholder="+1 (555) 000-0000"
                value={formData.phone}
                onChange={handleChange}
              />
            </div>
            <div>
              <label className="text-[11px] font-mono text-[#86868b] block mb-1">Years of Experience</label>
              <input
                type="number"
                name="years_exp"
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
                placeholder="4"
                min="0"
                value={formData.years_exp}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-mono text-[#86868b] block mb-1">Cover Note / Key Accomplishments</label>
            <textarea
              name="cover_letter"
              className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-3.5 py-2.5 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500"
              placeholder="Highlight your engineering accomplishments..."
              rows="4"
              value={formData.cover_letter}
              onChange={handleChange}
              required
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full mt-2 py-3 rounded-full bg-white text-black font-semibold text-xs hover:bg-neutral-200 transition-all flex items-center justify-center gap-2 active:scale-95"
          >
            {isSubmitting ? (
              <span className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <span>Submit Candidate Dossier</span>
                <Send className="w-3.5 h-3.5" />
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

const SuccessMessage = ({ job, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4" onClick={onClose}>
    <div className="apple-glass rounded-3xl p-8 max-w-md w-full border border-white/20 shadow-2xl text-center" onClick={(e) => e.stopPropagation()}>
      <div className="w-12 h-12 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center mx-auto mb-4">
        <CheckCircle2 className="w-6 h-6" />
      </div>
      <h3 className="text-xl font-bold text-white font-heading">Dossier Submitted</h3>
      <p className="text-xs text-[#86868b] mt-2 leading-relaxed">
        Thank you for your submission for <strong className="text-white">{job.title}</strong>. Our engineering leads will review your portfolio.
      </p>
      <button onClick={onClose} className="w-full mt-6 py-3 rounded-full bg-white text-black font-semibold text-xs hover:bg-neutral-200 transition-all">
        Close Window
      </button>
    </div>
  </div>
);

const CareersPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    document.title = "Careers & Engineering Roles — Quivix";
    getJobs()
      .then((res) => setJobs(Array.isArray(res) ? res : res.data || []))
      .catch((err) => console.error("Failed to load jobs:", err))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-black text-[#f5f5f7] min-h-screen pt-36 pb-24 font-sans selection:bg-blue-500/30">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        
        {/* Header */}
        <div className="max-w-3xl mb-16">
          <span className="text-xs font-mono uppercase tracking-widest text-blue-400">Careers & Engineering</span>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-white mt-3 font-heading">
            Build Extraordinary Products.
          </h1>
          <p className="mt-4 text-base text-[#86868b] leading-relaxed">
            We operate at the intersection of high-throughput systems, zero-trust security, and elegant user experience. Explore our open positions.
          </p>
        </div>

        {/* Benefits Row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          <div className="apple-glass rounded-2xl p-6 border border-white/[0.08]">
            <span className="text-blue-400 font-mono text-xs">01 • Remote Autonomy</span>
            <h3 className="text-base font-bold text-white font-heading mt-2">Work From Anywhere</h3>
            <p className="text-xs text-[#86868b] mt-1">Distributed global team with flexible asynchronous operations.</p>
          </div>
          <div className="apple-glass rounded-2xl p-6 border border-white/[0.08]">
            <span className="text-emerald-400 font-mono text-xs">02 • Equity Allocation</span>
            <h3 className="text-base font-bold text-white font-heading mt-2">Direct Venture Ownership</h3>
            <p className="text-xs text-[#86868b] mt-1">Generous stock options and performance bonus pools.</p>
          </div>
          <div className="apple-glass rounded-2xl p-6 border border-white/[0.08]">
            <span className="text-amber-400 font-mono text-xs">03 • Top-Tier Stack</span>
            <h3 className="text-base font-bold text-white font-heading mt-2">Uncompromised Quality</h3>
            <p className="text-xs text-[#86868b] mt-1">Latest hardware grants, hardware testing labs, and training budgets.</p>
          </div>
        </div>

        {/* Job Listings */}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between pb-4 border-b border-white/[0.08]">
            <h2 className="text-lg font-bold text-white font-heading">Open Positions</h2>
            <span className="text-xs font-mono text-[#86868b]">{jobs.length} Positions Available</span>
          </div>

          {loading ? (
            <div className="py-16 text-center text-xs font-mono text-[#86868b]">
              Loading Available Positions...
            </div>
          ) : jobs.length > 0 ? (
            jobs.map((job) => (
              <div
                key={job.id}
                className="apple-glass rounded-2xl p-6 sm:p-8 border border-white/[0.08] apple-card-hover flex flex-col md:flex-row md:items-center justify-between gap-6"
              >
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="text-lg font-bold text-white font-heading">{job.title}</h3>
                    <span className="text-[10px] font-mono text-blue-400 bg-blue-500/10 px-2.5 py-0.5 rounded-full border border-blue-500/20">
                      Full-Time
                    </span>
                  </div>
                  <p className="text-xs text-[#86868b] mt-2 leading-relaxed max-w-2xl">
                    {job.description}
                  </p>
                </div>

                <button
                  onClick={() => { setSelectedJob(job); setIsSubmitted(false); }}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-black font-semibold text-xs hover:bg-neutral-200 transition-all shrink-0 active:scale-95"
                >
                  <span>Apply Position</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          ) : (
            <div className="py-16 apple-glass rounded-2xl text-center border border-white/10 p-8">
              <p className="text-xs text-[#86868b]">No open engineering roles currently. Feel free to send your CV to support@quivixdigital.com.</p>
            </div>
          )}
        </div>

      </div>

      {selectedJob && !isSubmitted && (
        <ApplicationForm job={selectedJob} onClose={() => setSelectedJob(null)} onSubmitSuccess={() => setIsSubmitted(true)} />
      )}
      {selectedJob && isSubmitted && (
        <SuccessMessage job={selectedJob} onClose={() => setSelectedJob(null)} />
      )}
    </div>
  );
};

export default CareersPage;
